import PubSub from "pubsub-js";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, ButtonGroup, Col, Container, Dropdown, Form, InputGroup, Modal, Row } from "react-bootstrap";
import {
  FaBuilding,
  FaCaretDown,
  FaClone,
  FaCog,
  FaColumns,
  FaDesktop,
  FaEdit,
  FaEllipsisV,
  FaFilter,
  FaList,
  FaMobileAlt,
  FaPlus,
  FaQuestionCircle,
  FaStar,
  FaThumbtack,
  FaTimes,
  FaTrash
} from "react-icons/fa";
import DataApi from "../../api/DataApi";
import ObjectConfigApi from "../../api/ObjectConfigApi";
import TelecmiApi from "../../api/TelecmiApi";
import { buildTelecmiInitiatePayload } from "../../utils/telecmiInitiatePayload";
import ResizableTable from "../common/ResizableTable";
import AppToast from "../AppToast";
import { exportToExcel } from "../../utils/excelExport.js";
import "./css/ListView.css";
import EditLayoutModal from "./EditLayoutModal";
import EmailCampaignAudienceContentTab from "../email/EmailCampaignAudienceContentTab";
import EnhancedListViewDialog from "./EnhancedListViewDialog";
import KanbanView from "./KanbanView";
import { useListViewFilters } from "./hooks/useListViewFilters";
import ListViewFilterPanel from "./components/ListViewFilterPanel";
import { prepareTableData, getFilteredAndSortedRecords } from "./utils/listViewUtils";
import { getListViewStyleRowActions } from "../../utils/listViewRowActions";
import { useNavigate } from "react-router-dom";
import { createKanbanHandlers } from "./utils/kanbanHandlers";
import { PermissionCheckingContext } from "../common/PermissionProtectedRoute";
import { hasPackage } from "../../utils/entitlements";

const ListViewLayout = ({ objectName, location }) => {
  const navigate = useNavigate();
  const { permissionChecking } = useContext(PermissionCheckingContext);
  const [listDropdownOpen, setListDropdownOpen] = useState(false);
  const [dropdownOpenIndex, setDropdownOpenIndex] = useState(null);
  const [filters, setFilters] = useState({});
  const [fieldOrder, setFieldOrder] = useState([]);
  const [records, setRecords] = useState([]);

  console.log("objectName: ", objectName);
  const dropdownRef = useRef(null);
  const listBtnRef = useRef(null);
  const [listViewName, setListViewName] = useState("");
  const [listViewLabel, setListViewLabel] = useState("");
  const [listViews, setListViews] = useState([]);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingListView, setEditingListView] = useState(null);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloningListView, setCloningListView] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [objectLabel, setObjectLabel] = useState("");

  // EditLayout Modal state
  const [editModalShow, setEditModalShow] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [isCloneMode, setIsCloneMode] = useState(false);

  // Button loading states
  const [buttonLoadingStates, setButtonLoadingStates] = useState({
    edit: false,
    delete: false,
    default: false
  });

  const [fieldDataTypes, setFieldDataTypes] = useState({});
  const [allObjectFields, setAllObjectFields] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showEmailCampaignModal, setShowEmailCampaignModal] = useState(false);
  const [campaignName, setCampaignName] = useState("");
  const [campaignRecordId, setCampaignRecordId] = useState(null);
  const [campaignRecordData, setCampaignRecordData] = useState(null);
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  const [campaignError, setCampaignError] = useState("");

  // Toast notification state
  const [toast, setToast] = useState({
    show: false,
    message: '',
    variant: 'success'
  });
  const [toastId, setToastId] = useState(0);
  const [currentPlatform, setCurrentPlatform] = useState("web");

  const showToastMessage = (message, variant = 'success') => {
    console.log('# [AppToast] Showing:', variant, message);
    setToast({
      show: true,
      message,
      variant
    });
    setToastId(prev => prev + 1);
  };

  // Use filter hook
  const filterHook = useListViewFilters(objectName, listViews, listViewName);
  const {
    filterPanelOpen,
    externalFilters,
    externalFilterLogic,
    availableFields,
    activeFiltersCount,
    hasActiveFilters,
    toggleFilterPanel,
    addExternalFilter,
    removeExternalFilter,
    updateExternalFilter,
    getOperatorsForField,
    isOperatorValueRequired,
    getFieldDataType,
    applyExternalFilters,
    clearExternalFilters,
    setExternalFilters,
    setExternalFilterLogic,
    setFilterPanelOpen
  } = filterHook;

  // Load more states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(200);
  const [loading, setLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false); // false until first fetch for this object finishes
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Sorting state for ResizableTable
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  // Selected rows for ResizableTable (ids)
  const [selectedRows, setSelectedRows] = useState([]);

  // Bulk delete states (result shown in same modal; refresh only after user clicks Close)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [bulkDeleteResult, setBulkDeleteResult] = useState(null); // { deleted, total, errors: [{ id, error }], apiError?: string }
  const [deleteProgress, setDeleteProgress] = useState({
    total: 0,
    processed: 0,
    deleted: 0,
    errors: 0
  });
  const [bulkDeleteChildrenChecked, setBulkDeleteChildrenChecked] = useState(false);
  const [showDateHelpModal, setShowDateHelpModal] = useState(false);

  // Import trigger state
  const [triggerImport, setTriggerImport] = useState(false);
  const [showBulkCallModal, setShowBulkCallModal] = useState(false);
  const [bulkCallQueue, setBulkCallQueue] = useState([]);
  const [bulkCallCurrentIndex, setBulkCallCurrentIndex] = useState(-1);
  const [bulkCallIsRunning, setBulkCallIsRunning] = useState(false);
  const [bulkCallIsPaused, setBulkCallIsPaused] = useState(false);
  const [bulkCallDelaySeconds, setBulkCallDelaySeconds] = useState(15);
  const bulkCallTimerRef = useRef(null);
  const bulkCallQueueRef = useRef([]);
  const bulkCallPausedRef = useRef(false);
  const bulkCallModalOpenRef = useRef(false);
  const bulkCallDelayRef = useRef(15);

  // Kanban view states
  const [viewType, setViewType] = useState(null); // null until config loads
  const [kanbanConfig, setKanbanConfig] = useState(null);
  const [picklistOptions, setPicklistOptions] = useState([]);
  const [isUpdatingViewType, setIsUpdatingViewType] = useState(false);
  const [showKanbanHelpModal, setShowKanbanHelpModal] = useState(false);
  const canUseKanban = !!kanbanConfig?.groupByField;
  const effectiveViewType = viewType || 'list';

  // Request cancellation refs to prevent race conditions
  const abortControllerRef = useRef(null);
  const currentObjectNameRef = useRef(null);

  useEffect(() => {
    bulkCallQueueRef.current = bulkCallQueue;
  }, [bulkCallQueue]);

  useEffect(() => {
    bulkCallPausedRef.current = bulkCallIsPaused;
  }, [bulkCallIsPaused]);

  useEffect(() => {
    bulkCallModalOpenRef.current = showBulkCallModal;
  }, [showBulkCallModal]);

  useEffect(() => {
    bulkCallDelayRef.current = bulkCallDelaySeconds;
  }, [bulkCallDelaySeconds]);

  useEffect(() => {
    return () => {
      if (bulkCallTimerRef.current) {
        clearTimeout(bulkCallTimerRef.current);
      }
    };
  }, []);

  // Function to change view type and save preference
  const handleViewTypeChange = async (newViewType) => {
    if (newViewType === viewType || isUpdatingViewType) {
      return;
    }

    if (newViewType === 'kanban' && !canUseKanban) {
      setShowKanbanHelpModal(true);
      return;
    }

    const currentListView = listViews.find(view => view.name === listViewName);
    if (!currentListView) {
      setViewType(newViewType);
      setRefreshTrigger(prev => prev + 1);
      return;
    }

    const previousViewType = viewType;
    setViewType(newViewType);
    setIsUpdatingViewType(true);

    const updatedConfig = {
      ...currentListView.config,
      viewType: newViewType
    };

    try {
      const listViewApi = new DataApi(`objectconfig/listviews/${objectName}`);
      const response = await listViewApi.update(
        { config: updatedConfig },
        currentListView.id
      );

      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Unable to update list view');
      }

      setListViews(prev =>
        prev.map(view =>
          view.id === currentListView.id ? { ...view, config: updatedConfig } : view
        )
      );

      PubSub.publish("RECORD_SAVED_TOAST", {
        title: "View Preference Saved",
        message: `Default view updated to ${newViewType === 'kanban' ? 'Kanban' : 'List'}`
      });

      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error updating view type:', error);
      setViewType(previousViewType);
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "View Preference Error",
        message: error.response?.data?.error || error.message || "Failed to update default view"
      });
    } finally {
      setIsUpdatingViewType(false);
    }
  };


  const fetchObjectConfig = async () => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    currentObjectNameRef.current = objectName;

    try {
      // Use working endpoint: fetch configs and find object_label for this object
      const configsResponse = await ObjectConfigApi.fetchObjectConfigs();
      if (abortController.signal.aborted || currentObjectNameRef.current !== objectName) {
        return;
      }
      const configs = configsResponse?.data || [];
      const objectConfig = Array.isArray(configs) ? configs.find(c => (c.object_name || c.objectName) === objectName) : null;
      if (objectConfig?.object_label) {
        setObjectLabel(objectConfig.object_label);
      } else {
        setObjectLabel(objectName);
      }

      // Also fetch field configurations to get data types for formatting
      try {
        const fieldResponse = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);

        // Check if request was aborted
        if (abortController.signal.aborted || currentObjectNameRef.current !== objectName) {
          return;
        }

        const result = fieldResponse.data;
        const rawConfig = result?.config;
        const config = typeof rawConfig === 'string' ? (() => { try { return JSON.parse(rawConfig); } catch { return {}; } })() : (rawConfig || {});
        const hiddenFieldsSet = new Set(((config.hidden_fields || [])).map(f => (f || '').toLowerCase()).filter(Boolean));

        if (result?.fields?.length > 0) {
          const dataTypes = {};
          const allFields = [];
          const isReadOnlyField = (field) =>
            field?.is_readonly === true || field?.read_only === true || field?.readonly === true;
          const isImportableField = (field) => {
            const fieldName = (field?.field_name || '').toLowerCase();
            if (!fieldName) return false;
            if (fieldName === 'id') return true;
            return !isReadOnlyField(field);
          };
          const isNotHidden = (field) => !hiddenFieldsSet.has((field?.field_name || '').toLowerCase());

          result.fields.forEach(field => {
            dataTypes[field.field_name] = field.data_type || 'text';
          });

          result.fields.filter(isImportableField).filter(isNotHidden).forEach(field => {
            allFields.push({
              value: field.field_name,
              label: field.field_label || field.field_name,
              type: field.data_type || 'text',
              data_type: field.data_type || 'text',
              is_unique: field.is_unique || false,
              is_required: field.is_required || false,
              is_readonly: field.is_readonly || false
            });
          });

          setFieldDataTypes(dataTypes);
          setAllObjectFields(allFields);
          console.log("Field data types loaded:", dataTypes);
          console.log("All object fields loaded:", allFields);
        }
      } catch (fieldError) {
        // Ignore abort errors
        if (fieldError.name === 'AbortError' || fieldError.name === 'CanceledError') {
          return;
        }
        console.error("Error fetching field configurations:", fieldError);
      }
    } catch (error) {
      // Ignore abort errors
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        return;
      }
      console.error("Error fetching object config:", error);
      // Only update state if this is still the current object
      if (currentObjectNameRef.current === objectName) {
        setObjectLabel(objectName);
      }
    }
  };

  // Function to fetch list views and data
  const fetchListViewData = async (page = 1, append = false) => {
    console.log("fetchListViewData called with objectName:", objectName, "listViewName:", listViewName, "page:", page, "platform:", currentPlatform);

    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    currentObjectNameRef.current = objectName;

    const configApi = new DataApi(`objectconfig/listviews/${objectName}`);

    try {
      setLoading(true);

      // First, try to get list views for current platform
      const listViewResponse = await configApi.fetchAll(`platform=${currentPlatform}`, {
        signal: abortController.signal
      });

      // Check if request was aborted
      if (abortController.signal.aborted || currentObjectNameRef.current !== objectName) {
        return;
      }

      let fetchedListViews = listViewResponse?.data?.data || [];

      // If no list views found for current platform, fetch all list views as fallback
      if (fetchedListViews.length === 0) {
        console.log("No list views found for platform:", currentPlatform, "- fetching all list views");
        const allListViewsResponse = await configApi.fetchAll('', {
          signal: abortController.signal
        });

        // Check if request was aborted
        if (abortController.signal.aborted || currentObjectNameRef.current !== objectName) {
          return;
        }

        fetchedListViews = allListViewsResponse?.data?.data || [];
      }

      setListViews(fetchedListViews);

      // Find the appropriate list view to use
      let selectedListView;

      if (listViewName && listViewName.trim() !== "") {
        // If we have a specific list view name, try to find it
        selectedListView = fetchedListViews.find((v) => v.name === listViewName);
        console.log("Looking for specific list view:", listViewName);
      }

      // If not found or no specific name, use default or first available
      if (!selectedListView) {
        selectedListView = fetchedListViews.find((v) => v.is_default) || fetchedListViews[0];
        console.log("Using default/first list view:", selectedListView?.name);
      }

      console.log("Available list views:", fetchedListViews.map(lv => ({ name: lv.name, is_default: lv.is_default })));
      console.log("Selected list view:", selectedListView);

      // Update the label and name for display
      if (selectedListView) {
        setListViewName(selectedListView.name);
        setListViewLabel(selectedListView.label || selectedListView.name);

        // Load filters from list view configuration - handled by hook
      }

      // Use the new listview API endpoint with specific listview name
      const listViewNameParam = selectedListView?.name;
      console.log("Using listview name:", listViewNameParam);
      console.log("Selected list view:", selectedListView);

      if (!listViewNameParam) {
        console.log("No valid list view name found, skipping API call");
        setRecords([]);
        setHasMore(false);
        return;
      }

      if (!selectedListView) {
        console.log("No selected list view found, skipping API call");
        setRecords([]);
        setHasMore(false);
        return;
      }

      // Calculate offset for pagination
      const offset = (page - 1) * pageSize;

      // Use listview endpoint: returns records, total count, and listView (handles filters, ownership, kanban fields)
      const apiPath = `object/listview/${objectName}/${listViewNameParam}`;
      const listViewApi = new DataApi(apiPath);
      const queryParams = new URLSearchParams();
      queryParams.append("limit", pageSize);
      queryParams.append("offset", offset);
      queryParams.append("platform", currentPlatform);
      if (viewType) {
        queryParams.append("viewType", viewType);
      }
      const result = await listViewApi.fetchAll(queryParams.toString(), {
        signal: abortController.signal
      });

      // Check if request was aborted before processing results
      if (abortController.signal.aborted || currentObjectNameRef.current !== objectName) {
        console.log("Request was aborted or objectName changed, ignoring results");
        return;
      }

      console.log("List view API result: ", result);

      if (result?.data?.success) {
        const newRecords = result.data.data.records;
        const totalCount = result.data.data.total || 0;
        const returnedListView = result.data.data.listView;
        console.log("Records from listview API: ", newRecords);
        console.log("Total count from API:", totalCount);

        if (newRecords && newRecords.length > 0) {
          if (returnedListView) {
            setListViews((prev) => {
              const existingIndex = prev.findIndex(
                (view) => view.id === returnedListView.id
              );
              if (existingIndex !== -1) {
                const updatedViews = [...prev];
                updatedViews[existingIndex] = {
                  ...prev[existingIndex],
                  ...returnedListView,
                };
                return updatedViews;
              }
              return prev;
            });
            if (returnedListView.name) {
              setListViewName(returnedListView.name);
              setListViewLabel(returnedListView.label || returnedListView.name);
            }
          }
          // Use fields from list view config instead of all record keys
          const configFields = selectedListView?.config?.fields || [];
          if (configFields.length > 0) {
            setFieldOrder(configFields);
          } else {
            const firstRecord = newRecords[0];
            const orderedFields = Object.keys(firstRecord).filter(key => key !== 'id');
            setFieldOrder(orderedFields);
          }

          if (append) {
            setRecords(prev => [...prev, ...newRecords]);
            setIsLoadingMore(false);
          } else {
            setRecords(newRecords);
          }

          const currentTotal = append ? (records.length + newRecords.length) : newRecords.length;
          setHasMore(currentTotal < totalCount);
        } else {
          if (append) {
            setRecords(prev => prev);
          } else {
            setRecords([]);
          }
          setHasMore(false);
        }
      } else {
        console.error("List view API returned error:", result?.data);
        if (!append) {
          setRecords([]);
        }
        setHasMore(false);
      }
    } catch (err) {
      // Ignore abort errors - they're expected when switching tabs
      if (err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        console.log("Request was cancelled (tab switch detected)");
        return;
      }
      console.error("Fetch error:", err);
      // Only update state if this is still the current object
      if (currentObjectNameRef.current === objectName) {
        if (!append) {
          setRecords([]);
        }
        setHasMore(false);
      }
    } finally {
      // Only update loading state if this is still the current object
      if (currentObjectNameRef.current === objectName) {
        setLoading(false);
        // Don't mark initial load complete if this request was aborted (e.g. listViewName changed
        // and a new fetch started), otherwise we'd flash "No records found" before the new fetch finishes
        if (!abortController.signal.aborted) {
          setInitialLoadComplete(true);
        }
      }
    }
  };

  // Effect to handle initial list view selection
  useEffect(() => {
    if (listViews.length > 0 && (!listViewName || listViewName.trim() === "")) {
      const defaultListView = listViews.find((v) => v.is_default) || listViews[0];
      if (defaultListView) {
        console.log("Setting initial list view name to:", defaultListView.name);
        setListViewName(defaultListView.name);
        setListViewLabel(defaultListView.label || defaultListView.name);
      }
    }
  }, [listViews, listViewName, objectName]);

  // Handle URL parameter changes (including refresh parameter)
  useEffect(() => {
    if (location) {
      const searchParams = new URLSearchParams(location.search);
      const refreshParam = searchParams.get('refresh');

      if (refreshParam) {
        console.log("Refresh parameter detected:", refreshParam);
        // Reset all component state when refresh parameter is present
        setRecords([]);
        setListViewName("");
        setListViewLabel("");
        setListViews([]);
        setFilters({});
        setFieldOrder([]);
        setCurrentPage(1);
        setSearchTerm("");
        setFieldDataTypes({});
        setAllObjectFields([]);
        setObjectLabel("");
        setInitialLoadComplete(false);

        // Trigger a fresh data fetch
        setRefreshTrigger(prev => prev + 1);
      }
    }
  }, [location]);

  // Handle objectName changes - reset state when switching between different objects
  useEffect(() => {
    console.log("ObjectName changed to:", objectName);

    // Cancel any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Update current object name ref
    currentObjectNameRef.current = objectName;

    // Reset component state when objectName changes
    setRecords([]);
    setListViewName("");
    setListViewLabel("");
    setListViews([]);
    setFilters({});
    setFieldOrder([]);
    setCurrentPage(1);
    setSearchTerm("");
    setFieldDataTypes({});
    setAllObjectFields([]);
    setObjectLabel("");
    setViewType(null);
    setKanbanConfig(null);

    // Reset button loading states
    setButtonLoadingStates({
      edit: false,
      delete: false,
      default: false
    });

    // Reset modal states
    setEditModalShow(false);
    setEditingRecordId(null);
    setNewDialogOpen(false);
    setEditDialogOpen(false);
    setEditingListView(null);

    // Reset filter panel
    setFilterPanelOpen(false);
    setListDropdownOpen(false);
    setDropdownOpenIndex(null);
    setInitialLoadComplete(false); // show loader until first fetch completes for new object
  }, [objectName]);

  useEffect(() => {
    if (!objectName) {
      console.log("Skipping fetch - objectName is not defined");
      return;
    }
    // Show loader immediately so we never flash "No records found" before data loads
    setLoading(true);
    setInitialLoadComplete(false);
    console.log("useEffect triggered - objectName:", objectName, "listViewName:", listViewName, "refreshTrigger:", refreshTrigger, "platform:", currentPlatform);
    fetchObjectConfig();
    fetchListViewData(1, false); // Reset to first page when changing list view
    setCurrentPage(1); // Reset current page

    // Cleanup: abort requests on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [objectName, listViewName, refreshTrigger, currentPlatform]);

  // Subscribe to PubSub toast events
  useEffect(() => {
    const savedToken = PubSub.subscribe("RECORD_SAVED_TOAST", (msg, data) => {
      showToastMessage(
        data.message || 'Operation completed successfully',
        'success'
      );
    });

    const errorToken = PubSub.subscribe("RECORD_ERROR_TOAST", (msg, data) => {
      showToastMessage(data.message || 'An error occurred', 'danger');
    });

    return () => {
      PubSub.unsubscribe(savedToken);
      PubSub.unsubscribe(errorToken);
    };
  }, []);

  // Simple click outside handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (listDropdownOpen && listBtnRef.current && !listBtnRef.current.contains(e.target)) {
        setListDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [listDropdownOpen]);

  // Loading behavior
  useEffect(() => {
    if (isLoadingMore) {
      const timer = setTimeout(() => {
        setIsLoadingMore(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isLoadingMore]);

  // Listen for custom events from modals/popups
  useEffect(() => {
    const handleListViewRefresh = (event) => {
      if (event.detail && event.detail.objectName === objectName) {
        // Refresh the list view when a record is saved from modal/popup
        console.log('Refreshing list view after record save:', event.detail);
        setRefreshTrigger(prev => prev + 1);
      }
    };

    window.addEventListener('listViewRefresh', handleListViewRefresh);
    return () => window.removeEventListener('listViewRefresh', handleListViewRefresh);
  }, [objectName]);

  // Load view type and Kanban config from list view configuration
  useEffect(() => {
    const currentListView = listViews.find(view => view.name === listViewName);

    if (currentListView?.config) {
      const hasKanban = !!currentListView.config.kanban?.groupByField;
      setKanbanConfig(currentListView.config.kanban || null);

      if (hasKanban) {
        const configViewType = currentListView.config.viewType === 'kanban' ? 'kanban' : 'list';
        setViewType(configViewType);
      } else {
        setViewType('list');
      }
    } else {
      setKanbanConfig(null);
      setViewType(null);
    }
  }, [listViews, listViewName]);

  // Load picklist options when Kanban config is available
  useEffect(() => {
    const loadPicklistOptions = async () => {
      if (kanbanConfig?.groupByField) {
        try {
          const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
          const field = response.data.fields.find(f => f.field_name === kanbanConfig.groupByField);

          if (field) {
            // Use picklist_values_data if available (from picklist_values table with sort_order)
            // Otherwise fall back to picklist_values (legacy JSONB field)
            const picklistValues = field.picklist_values_data || field.picklist_values || [];

            console.log('Loaded picklist options for Kanban:', {
              field: kanbanConfig.groupByField,
              count: picklistValues.length,
              values: picklistValues
            });

            setPicklistOptions(picklistValues);
          } else {
            console.warn('Kanban groupByField not found in object fields:', kanbanConfig.groupByField);
            setPicklistOptions([]);
          }
        } catch (error) {
          console.error('Error loading picklist options:', error);
          setPicklistOptions([]);
        }
      } else {
        setPicklistOptions([]);
      }
    };
    loadPicklistOptions();
  }, [kanbanConfig, objectName]);

  const handleListViewSave = async (config) => {
    console.log("config: ", config);

    try {
      const listViewApi = new DataApi(`objectconfig/listviews/${objectName}`);
      const response = await listViewApi.create(config);
      console.log("List view saved:", response.data);
      PubSub.publish("RECORD_SAVED_TOAST", {
        title: "List View Created",
        message: "List view saved successfully"
      });

      // Set the newly created listview as the selected one
      setListViewName(config.name);
      setListViewLabel(config.label || config.name);

      // Trigger refresh of listview data
      setRefreshTrigger(prev => prev + 1);

      // Close all dialogs (new, clone)
      setNewDialogOpen(false);
      setCloneDialogOpen(false);
      setCloningListView(null);
    } catch (error) {
      console.error("Error saving list view:", error);
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Save Error",
        message: "Error saving list view"
      });
    }
  };

  const handleListViewUpdate = async (listViewId, config) => {
    console.log("Updating list view:", listViewId, config);

    try {
      const listViewApi = new DataApi(`objectconfig/listviews/${objectName}`);
      const response = await listViewApi.update(config, listViewId);
      console.log("List view updated:", response.data);
      PubSub.publish("RECORD_SAVED_TOAST", {
        title: "List View Updated",
        message: "List view updated successfully"
      });

      // If this is the currently selected list view, update the labels
      if (listViewId === editingListView?.id) {
        setListViewName(config.name);
        setListViewLabel(config.label || config.name);
      }

      // Trigger refresh of listview data
      setRefreshTrigger(prev => prev + 1);

      // Close the dialog
      setEditDialogOpen(false);
      setEditingListView(null);
    } catch (error) {
      console.error("Error updating list view:", error);
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Update Error",
        message: "Error updating list view"
      });
    }
  };

  const handleEditListView = (listView) => {
    setButtonLoadingStates(prev => ({ ...prev, edit: true }));
    try {
      setEditingListView(listView);
      setEditDialogOpen(true);
    } finally {
      // Reset loading state after a short delay to prevent rapid clicking
      setTimeout(() => {
        setButtonLoadingStates(prev => ({ ...prev, edit: false }));
      }, 500);
    }
  };

  const handleCloneListView = (listView) => {
    console.log("Cloning list view:", listView);
    setCloningListView({
      ...listView,
      // Suggest a new name with suffix
      name: `${listView.name}_copy`,
      label: `${listView.label || listView.name} (Copy)`,
      is_default: false // Cloned view shouldn't be default
    });
    setCloneDialogOpen(true);
  };

  // Helper function to check if a list view can be deleted
  const canDeleteListView = (listView) => {
    // Cannot delete if it's the default list view
    if (listView.is_default) {
      return { canDelete: false, reason: "Cannot delete the default list view" };
    }

    // Cannot delete if it's a standard list view (starts with "all_")
    if (listView.name && listView.name.toLowerCase().startsWith('all_')) {
      return { canDelete: false, reason: "Cannot delete standard list views" };
    }

    // Cannot delete if it's the only list view
    if (listViews.length <= 1) {
      return { canDelete: false, reason: "Cannot delete the last list view" };
    }

    return { canDelete: true, reason: null };
  };

  const handleDeleteListView = async (listView) => {
    // Check if list view can be deleted
    const { canDelete, reason } = canDeleteListView(listView);

    if (!canDelete) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Cannot Delete",
        message: reason
      });
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the list view "${listView.label || listView.name}"?`)) {
      return;
    }

    setButtonLoadingStates(prev => ({ ...prev, delete: true }));

    try {
      const listViewApi = new DataApi(`objectconfig/listviews/${objectName}`);
      const response = await listViewApi.delete(listView.id);
      console.log("List view deleted:", response.data);
      PubSub.publish("RECORD_SAVED_TOAST", {
        title: "List View Deleted",
        message: "List view deleted successfully"
      });

      // If this was the currently selected list view, reset to default
      if (listView.name === listViewName) {
        setListViewName("");
        setListViewLabel("");
      }

      // Clear editing state if this was being edited
      if (listView.id === editingListView?.id) {
        setEditingListView(null);
        setEditDialogOpen(false);
      }

      // Trigger refresh of listview data
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Error deleting list view:", error);
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Delete Error",
        message: "Error deleting list view: " + (error.response?.data?.error || error.message)
      });
    } finally {
      setButtonLoadingStates(prev => ({ ...prev, delete: false }));
    }
  };

  // Load more function
  const loadMore = () => {
    console.log("loadMore called");
    console.log("loading ", loading);
    console.log("hasMore ", hasMore);
    if (!loading && hasMore) {
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchListViewData(nextPage, true);
    }
  };

  // Get filtered and sorted records using utility
  const getFilteredAndSortedRecordsLocal = () => {
    return getFilteredAndSortedRecords(records, searchTerm, filters, sortBy, sortOrder);
  };

  // Prepare data for ResizableTable using utility
  const prepareTableDataLocal = () => {
    const filteredSorted = getFilteredAndSortedRecordsLocal();
    return prepareTableData(filteredSorted, objectName, fieldDataTypes);
  };

  const recordLinkField = useMemo(() => {
    if (!fieldOrder || fieldOrder.length === 0) {
      return 'name';
    }
    if (fieldOrder.includes('name')) {
      return 'name';
    }
    return fieldOrder[0];
  }, [fieldOrder]);

  // Prepare columns for ResizableTable
  const prepareTableColumns = () => {
    if (!records || records.length === 0) return [];
    const columns = [];

    // Only show columns that are explicitly in fieldOrder (from config.fields)
    // ID is included ONLY if it's in the configured fields
    fieldOrder.forEach((key) => {
      if (key === 'id') {
        // ID field with fixed width
        columns.push({ field: 'id', label: records[0]?.id?.label || 'ID', width: '90px', sortable: true });
      } else if (records[0]?.[key] !== undefined) {
        // Regular field - only if it exists in the record
        const dataType = fieldDataTypes[key] || records[0]?.[key]?.type || '';
        const isNumeric = ['currency', 'number', 'decimal', 'integer', 'rollupsummary'].includes(dataType?.toLowerCase());
        columns.push({
          field: key,
          label: records[0]?.[key]?.label || key,
          sortable: true,
          dataType: dataType,
          align: isNumeric ? 'right' : 'left'
        });
      }
    });

    return columns;
  };

  // Handler for Export action
  const handleExport = () => {
    const tableData = prepareTableDataLocal();
    const columns = prepareTableColumns();

    // Filter data if rows are selected
    const exportData = selectedRows.length > 0
      ? tableData.filter(record => {
        const recordId = record.id?.value || record.id;
        return selectedRows.includes(recordId);
      })
      : tableData;

    exportToExcel(
      exportData,
      columns,
      selectedRows,
      `${objectName}_records`,
      objectName
    );
  };

  // Handler for Import action
  const handleImport = () => {
    if (!objectName) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Import Error",
        message: "Object name is required for import"
      });
      return;
    }
    // Trigger import modal in ResizableTable
    setTriggerImport(true);
  };

  // Reset import trigger after it's been handled
  const handleImportTriggered = () => {
    setTriggerImport(false);
  };

  // Header actions for ResizableTable - keep delete/export actions to enable checkboxes
  // The actual functionality is in the main action bar, but we need these for checkbox visibility
  // We'll filter these out in ResizableTable by checking for a hidden flag
  const getHeaderActions = () => [
    {
      label: 'Delete',
      icon: 'fas fa-trash',
      variant: 'danger',
      disabled: true,
      onClick: () => { }, // No-op since we handle it in main bar
      hidden: true // Flag to hide this action
    },
    {
      label: 'Export',
      icon: 'fas fa-download',
      variant: 'primary',
      disabled: true,
      onClick: () => { }, // No-op since we handle it in main bar
      hidden: true // Flag to hide this action
    }
  ];

  const getRowActions = () => getListViewStyleRowActions(objectName);

  const handleRowSelectionChange = (ids) => setSelectedRows(ids);
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };


  const handleSetAsDefault = async (listViewId, listViewName) => {
    setButtonLoadingStates(prev => ({ ...prev, default: true }));

    try {
      console.log("Setting list view as default:", listViewName);

      // Check if this list view is already the default
      const currentListView = listViews.find(view => view.id === listViewId);
      if (currentListView && currentListView.is_default) {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Already Default",
          message: `${listViewName} is already the default list view`
        });
        return;
      }

      // Call API to set this list view as default
      const configApi = new DataApi(`objectconfig/listviews/${objectName}`);
      const response = await configApi.update({ is_default: true }, listViewId);

      if (response?.data?.success) {
        console.log("List view set as default successfully");

        // Refresh the list views to get updated data
        setRefreshTrigger(prev => prev + 1);

        // Show success message
        PubSub.publish("RECORD_SAVED_TOAST", {
          title: "Default Set",
          message: `${listViewName} has been set as the default list view`
        });
      } else {
        console.error("Failed to set list view as default");
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Set Default Error",
          message: "Failed to set list view as default"
        });
      }
    } catch (error) {
      console.error("Error setting list view as default:", error);
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Set Default Error",
        message: "Error setting list view as default: " + (error.response?.data?.error || error.message)
      });
    } finally {
      setButtonLoadingStates(prev => ({ ...prev, default: false }));
    }
  };

  // Bulk email handler
  const handleBulkEmail = () => {
    setShowEmailCampaignModal(true);
    setCampaignName("");
    setCampaignRecordId(null);
    setCampaignRecordData(null);
    setCampaignError("");
  };

  const getRecordFieldValue = (record, fieldName) => {
    if (!record || !fieldName) return "";
    const raw = record[fieldName];
    if (raw && typeof raw === "object" && Object.prototype.hasOwnProperty.call(raw, "value")) {
      return raw.value ?? "";
    }
    return raw ?? "";
  };

  const normalizePhoneNumber = (value) => {
    return String(value || "").replace(/\D/g, "");
  };

  const getPhoneCandidates = () => {
    const configuredPhoneFields = (allObjectFields || [])
      .filter((field) => String(field?.type || field?.data_type || "").toLowerCase() === "phone")
      .map((field) => String(field?.value || "").trim())
      .filter(Boolean);
    return [
      ...configuredPhoneFields,
      "phone",
      "mobile",
      "mobile_no",
      "mobile_number",
      "contact_no",
      "phone_number",
      "alternate_phone",
    ];
  };

  const getContactDisplayName = (record) => {
    const nameCandidates = ["name", "full_name", "company"];
    for (const key of nameCandidates) {
      const val = String(getRecordFieldValue(record, key) || "").trim();
      if (val) return val;
    }
    const firstName = String(getRecordFieldValue(record, "first_name") || "").trim();
    const lastName = String(getRecordFieldValue(record, "last_name") || "").trim();
    const full = `${firstName} ${lastName}`.trim();
    return full || "Unnamed Contact";
  };

  const buildBulkCallQueue = () => {
    const selectedIdSet = new Set(selectedRows.map((id) => String(id)));
    const phoneCandidates = getPhoneCandidates();

    const queue = (records || [])
      .filter((record) => {
        const recordId = record?.id?.value || record?.id;
        return selectedIdSet.has(String(recordId));
      })
      .map((record) => {
        const recordId = record?.id?.value || record?.id;
        let chosenPhoneRaw = "";
        let chosenPhone = "";

        for (const fieldName of phoneCandidates) {
          const rawValue = getRecordFieldValue(record, fieldName);
          const normalized = normalizePhoneNumber(rawValue);
          if (normalized) {
            chosenPhoneRaw = String(rawValue);
            chosenPhone = normalized;
            break;
          }
        }

        return {
          id: String(recordId),
          name: getContactDisplayName(record),
          phone: chosenPhone,
          phoneDisplay: chosenPhoneRaw || "-",
          status: chosenPhone ? "pending" : "skipped",
          message: chosenPhone ? "" : "No valid phone number",
        };
      });

    return queue;
  };

  const getNextPendingIndex = (startIndex = 0) => {
    const queue = bulkCallQueueRef.current || [];
    for (let index = startIndex; index < queue.length; index += 1) {
      if (queue[index]?.status === "pending") {
        return index;
      }
    }
    return -1;
  };

  const closeBulkCallModal = () => {
    if (bulkCallTimerRef.current) {
      clearTimeout(bulkCallTimerRef.current);
      bulkCallTimerRef.current = null;
    }
    setBulkCallIsRunning(false);
    setBulkCallIsPaused(false);
    setBulkCallCurrentIndex(-1);
    setShowBulkCallModal(false);
  };

  const dialBulkCallAtIndex = async (index) => {
    const queue = bulkCallQueueRef.current || [];
    const entry = queue[index];
    if (!entry || !entry.phone || !bulkCallModalOpenRef.current) {
      setBulkCallIsRunning(false);
      return;
    }

    setBulkCallCurrentIndex(index);
    setBulkCallQueue((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, status: "calling", message: "Dialing..." } : item
      )
    );

    try {
      const toNum = Number(entry.phone);
      const built = buildTelecmiInitiatePayload(toNum, {
        crm: true,
        bulk_call: true,
        object_name: objectName,
        record_id: entry.id,
      });
      if (built.error) {
        setBulkCallQueue((prev) =>
          prev.map((item, i) =>
            i === index ? { ...item, status: "failed", message: built.error } : item
          )
        );
      } else {
        const response = await TelecmiApi.initiateCall(built.payload);

        const apiMessage =
          response?.data?.data?.msg ||
          response?.data?.msg ||
          response?.data?.message ||
          response?.data?.data?.message ||
          "Call initiated.";

        setBulkCallQueue((prev) =>
          prev.map((item, i) =>
            i === index ? { ...item, status: "completed", message: apiMessage } : item
          )
        );
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.data?.msg ||
        error?.response?.data?.msg ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to initiate call.";

      setBulkCallQueue((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, status: "failed", message: errorMessage } : item
        )
      );
    }

    const nextIndex = getNextPendingIndex(index + 1);
    if (!bulkCallModalOpenRef.current || nextIndex === -1) {
      setBulkCallIsRunning(false);
      setBulkCallIsPaused(false);
      setBulkCallCurrentIndex(-1);
      return;
    }

    if (bulkCallPausedRef.current) {
      return;
    }

    bulkCallTimerRef.current = setTimeout(() => {
      dialBulkCallAtIndex(nextIndex);
    }, Math.max(1, Number(bulkCallDelayRef.current) || 1) * 1000);
  };

  const startBulkAutoCall = async () => {
    const nextIndex = getNextPendingIndex(0);
    if (nextIndex === -1) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Bulk Call",
        message: "No callable contacts found in your selected records.",
      });
      return;
    }
    setBulkCallIsRunning(true);
    setBulkCallIsPaused(false);
    await dialBulkCallAtIndex(nextIndex);
  };

  const pauseBulkAutoCall = () => {
    if (bulkCallTimerRef.current) {
      clearTimeout(bulkCallTimerRef.current);
      bulkCallTimerRef.current = null;
    }
    setBulkCallIsPaused(true);
  };

  const resumeBulkAutoCall = async () => {
    const nextIndex = getNextPendingIndex(Math.max(0, bulkCallCurrentIndex + 1));
    if (nextIndex === -1) {
      setBulkCallIsRunning(false);
      setBulkCallIsPaused(false);
      return;
    }
    setBulkCallIsRunning(true);
    setBulkCallIsPaused(false);
    await dialBulkCallAtIndex(nextIndex);
  };

  // Bulk call handler
  const handleBulkCall = () => {
    if (!hasPackage("telecmi") && !hasPackage("callyzer")) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Bulk Call",
        message: "CTI package is not enabled for your organization.",
      });
      return;
    }
    if (selectedRows.length === 0) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Bulk Call",
        message: "Please select at least one record to start a bulk call.",
      });
      return;
    }

    const queue = buildBulkCallQueue();
    setBulkCallQueue(queue);
    setBulkCallCurrentIndex(-1);
    setBulkCallIsRunning(false);
    setBulkCallIsPaused(false);
    setShowBulkCallModal(true);
  };

  const handleCreateEmailCampaign = async () => {
    if (!campaignName.trim()) {
      setCampaignError("Campaign name is required.");
      return;
    }

    if (!listViewName) {
      setCampaignError("Please select a list view.");
      return;
    }

    setIsCreatingCampaign(true);
    setCampaignError("");
    try {
      const dataApi = new DataApi("object/email_campaign");
      const payload = {
        name: campaignName.trim(),
        target_object: objectName,
        list_view_name: listViewName,
        status: "Draft",
      };
      const response = await dataApi.create(payload);
      const createdId =
        response?.data?.data?.id ||
        response?.data?.data?.recordId ||
        response?.data?.data?.data?.id ||
        response?.data?.data?.data?.recordId;

      if (!createdId) {
        setCampaignError("Failed to create email campaign.");
        return;
      }

      setCampaignRecordId(createdId);
      const viewApi = new DataApi("object/view/email_campaign");
      const recordResponse = await viewApi.fetchById(createdId);
      setCampaignRecordData(recordResponse?.data?.data || null);
    } catch (error) {
      console.error("Error creating email campaign:", error);
      setCampaignError(error?.message || "Failed to create email campaign.");
    } finally {
      setIsCreatingCampaign(false);
    }
  };

  // Bulk delete handler
  const handleBulkDelete = () => {
    if (selectedRows.length === 0) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "No Selection",
        message: "Please select records to delete."
      });
      return;
    }
    setBulkDeleteResult(null);
    setShowDeleteConfirmModal(true);
  };

  // Confirm bulk delete; keep modal open, show loader then result (counts + reasons); refresh only when user clicks Close
  const confirmBulkDelete = async () => {
    if (selectedRows.length === 0) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "No Selection",
        message: "No records selected for deletion."
      });
      return;
    }

    const batchSize = 50;
    setIsDeleting(true);
    setBulkDeleteResult(null);
    setDeleteProgress({
      total: selectedRows.length,
      processed: 0,
      deleted: 0,
      errors: 0
    });

    try {
      const dataApi = new DataApi('object');
      const aggregate = {
        total: selectedRows.length,
        deleted: 0,
        errors: []
      };

      for (let i = 0; i < selectedRows.length; i += batchSize) {
        const batch = selectedRows.slice(i, i + batchSize);
        const response = await dataApi.deleteWithBody(`/${objectName}/bulk-delete`, {
          ids: batch,
          options: {
            batchSize,
            continueOnError: true,
            cascadeDeleteChildren: bulkDeleteChildrenChecked
          }
        });

        if (!response?.data?.success) {
          const msg = response?.data?.message || "Failed to delete record";
          batch.forEach(id => {
            aggregate.errors.push({ id, error: msg });
          });
        } else {
          const result = response.data.data;
          aggregate.deleted += result.deleted || 0;
          if (result.errors && result.errors.length > 0) {
            aggregate.errors.push(...result.errors);
          }
        }

        setDeleteProgress(prev => ({
          total: prev.total,
          processed: Math.min(prev.processed + batch.length, prev.total),
          deleted: aggregate.deleted,
          errors: aggregate.errors.length
        }));
      }

      setBulkDeleteResult({
        deleted: aggregate.deleted,
        total: aggregate.total,
        errors: aggregate.errors,
        apiError: null
      });
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Error deleting records. Please try again.";
      setBulkDeleteResult({
        deleted: 0,
        total: selectedRows.length,
        errors: [],
        apiError: msg
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Close bulk delete modal; if result was shown, clear selection and refresh list only then
  const cancelBulkDelete = () => {
    if (bulkDeleteResult) {
      setSelectedRows([]);
      setRefreshTrigger(prev => prev + 1);
    }
    setBulkDeleteResult(null);
    setBulkDeleteChildrenChecked(false);
    setShowDeleteConfirmModal(false);
  };

  // Kanban handlers using utility
  const {
    handleKanbanUpdateRecord,
    handleKanbanEdit,
    handleKanbanClone,
    handleKanbanDelete
  } = createKanbanHandlers(objectName, setEditingRecordId, setIsCloneMode, setEditModalShow, setRefreshTrigger);

  return (
    <>
      <div className="sf-header list-view-header">
        <div className="sf-top">
          <div className="sf-title" style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
            <FaBuilding className="icon" />
            <div>
              <div style={{ fontSize: "18px", fontWeight: "700", color: "#2c3e50" }}>{objectLabel}</div>
              <div style={{ fontSize: "11px", color: "#8c8c8c", marginTop: "2px" }}>List</div>
            </div>
            {/* List View Selector Dropdown */}
            <div className="list-view-selector" >
              <Dropdown style={{ border: "1px solid #eeee" }} onToggle={(isOpen) => setListDropdownOpen(isOpen)}>
                <Dropdown.Toggle
                  variant="light"
                  className={`list-view-button ${listViewName ? 'active' : ''}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#032d60',
                    background: 'white',
                    border: '1px solid rgb(0, 0, 0)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    minWidth: '200px',
                    boxShadow: 'none',
                    outline: 'none'
                  }}
                >
                  <span className="list-view-name">{listViewLabel || 'Select List View'}</span>
                </Dropdown.Toggle>

                <Dropdown.Menu
                  style={{
                    width: '250px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid #d8dde6',
                    borderRadius: '4px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    padding: '8px 0',
                    marginTop: '4px'
                  }}
                >
                  {listViews?.map((view) => (
                    <Dropdown.Item
                      key={view.id}
                      onClick={() => {
                        console.log("Changing list view from", listViewName, "to", view.name);
                        setListViewName(view.name);
                        setListViewLabel(view.label || view.name);
                      }}
                      style={{
                        backgroundColor: view.name === listViewName ? 'white' : 'transparent',
                        fontSize: '14px',
                        padding: '10px 16px',
                        border: 'none',
                        color: view.name === listViewName ? '#0176d3' : '#032d60',
                        fontWeight: view.name === listViewName ? '600' : '400',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (view.name !== listViewName) {
                          e.target.style.backgroundColor = '#f8f9fa';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (view.name !== listViewName) {
                          e.target.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1', minWidth: '0' }}>
                          {view.is_default && (
                            <FaStar style={{ color: '#ffc107', fontSize: '12px', flexShrink: 0 }} />
                          )}
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{view.label || view.name}</span>
                          {view.platform && (
                            <Badge
                              bg={view.platform === 'mobile' ? 'info' : 'secondary'}
                              style={{ fontSize: '9px', padding: '2px 6px', flexShrink: 0 }}
                            >
                              {view.platform === 'mobile' ? (
                                <><FaMobileAlt size={8} /> Mobile</>
                              ) : (
                                <><FaDesktop size={8} /> Web</>
                              )}
                            </Badge>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, marginLeft: '8px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCloneListView(view);
                            }}
                            title="Clone List View"
                            style={{
                              padding: '2px 6px',
                              border: '1px solid #000000ff',
                              borderRadius: '3px',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              fontSize: '10px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <FaClone size={10} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (buttonLoadingStates.edit) return;
                              handleEditListView(view);
                            }}
                            disabled={buttonLoadingStates.edit}
                            title="Edit List View"
                            style={{
                              padding: '2px 6px',
                              border: '1px solid #d8dde6',
                              borderRadius: '3px',
                              backgroundColor: buttonLoadingStates.edit ? '#f8f9fa' : 'white',
                              color: buttonLoadingStates.edit ? '#6c757d' : '#706e6b',
                              cursor: buttonLoadingStates.edit ? 'not-allowed' : 'pointer',
                              fontSize: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              opacity: buttonLoadingStates.edit ? 0.6 : 1
                            }}
                          >
                            {buttonLoadingStates.edit ? (
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '8px', height: '8px' }}></span>
                            ) : (
                              <FaEdit size={10} />
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (buttonLoadingStates.delete || !canDeleteListView(view).canDelete) return;
                              handleDeleteListView(view);
                            }}
                            disabled={buttonLoadingStates.delete || !canDeleteListView(view).canDelete}
                            title={!canDeleteListView(view).canDelete ? canDeleteListView(view).reason : "Delete List View"}
                            style={{
                              padding: '2px 6px',
                              border: '1px solid #d8dde6',
                              borderRadius: '3px',
                              backgroundColor: (buttonLoadingStates.delete || !canDeleteListView(view).canDelete) ? '#f8f9fa' : 'white',
                              color: (buttonLoadingStates.delete || !canDeleteListView(view).canDelete) ? '#6c757d' : '#dc3545',
                              cursor: (buttonLoadingStates.delete || !canDeleteListView(view).canDelete) ? 'not-allowed' : 'pointer',
                              fontSize: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              opacity: (buttonLoadingStates.delete || !canDeleteListView(view).canDelete) ? 0.6 : 1
                            }}
                          >
                            {buttonLoadingStates.delete ? (
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '8px', height: '8px' }}></span>
                            ) : (
                              <FaTrash size={10} />
                            )}
                          </button>
                        </div>
                      </div>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            {/* List View Actions Dropdown - 3 Dots Menu (show when a view is selected or none exist yet so user can create one) */}
            {((listViews.length === 0) || listViewName) && (() => {
              const currentListView = listViews.find(view => view.name === listViewName);
              const isDefault = currentListView?.is_default || false;
              const canDelete = currentListView ? canDeleteListView(currentListView).canDelete : false;

              return (
                <Dropdown onToggle={(isOpen) => { }}>
                  <Dropdown.Toggle
                    variant="light"
                    className="no-caret"
                    style={{
                      padding: '8px',
                      border: '1px solid #d8dde6',
                      borderLeft: 'none',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      color: '#706e6b',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '36px',
                      height: '36px',
                      boxShadow: 'none',
                      outline: 'none',
                      marginLeft: '-20px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                      e.currentTarget.style.borderColor = '#adb5bd';
                      e.currentTarget.style.color = '#495057';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.borderColor = '#d8dde6';
                      e.currentTarget.style.color = '#706e6b';
                    }}
                    title="List View Actions"
                  >
                    <FaEllipsisV size={16} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    style={{
                      minWidth: '200px',
                      border: '1px solid #d8dde6',
                      borderRadius: '4px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      padding: '4px 0',
                      marginTop: '4px'
                    }}
                  >
                    <Dropdown.Item
                      onClick={() => setNewDialogOpen(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 16px',
                        cursor: 'pointer'
                      }}
                    >
                      <FaPlus size={16} style={{ color: '#0176d3' }} />
                      <span>New List View</span>
                    </Dropdown.Item>
                    {currentListView && (
                      <>
                    <Dropdown.Divider />
                    <Dropdown.Item
                      onClick={() => {
                        if (buttonLoadingStates.default) return;
                        if (currentListView) {
                          handleSetAsDefault(currentListView.id, currentListView.label || currentListView.name);
                        }
                      }}
                      disabled={buttonLoadingStates.default || isDefault}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 16px',
                        cursor: (buttonLoadingStates.default || isDefault) ? 'not-allowed' : 'pointer',
                        opacity: (buttonLoadingStates.default || isDefault) ? 0.6 : 1,
                        backgroundColor: isDefault ? '#e8f4ff' : 'transparent',
                        color: isDefault ? '#0176d3' : '#032d60'
                      }}
                    >
                      {buttonLoadingStates.default ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '14px', height: '14px' }}></span>
                          <span>Setting...</span>
                        </>
                      ) : (
                        <>
                          <FaThumbtack size={16} style={{ color: isDefault ? '#0176d3' : '#706e6b' }} />
                          <span>{isDefault ? 'Default List View' : 'Set as Default'}</span>
                        </>
                      )}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        if (buttonLoadingStates.edit) return;
                        if (currentListView) {
                          handleEditListView(currentListView);
                        }
                      }}
                      disabled={buttonLoadingStates.edit}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 16px',
                        cursor: buttonLoadingStates.edit ? 'not-allowed' : 'pointer',
                        opacity: buttonLoadingStates.edit ? 0.6 : 1
                      }}
                    >
                      {buttonLoadingStates.edit ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '14px', height: '14px' }}></span>
                          <span>Editing...</span>
                        </>
                      ) : (
                        <>
                          <FaEdit size={16} style={{ color: '#0176d3' }} />
                          <span>Edit List View</span>
                        </>
                      )}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        if (currentListView) {
                          handleCloneListView(currentListView);
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 16px',
                        cursor: 'pointer'
                      }}
                    >
                      <FaClone size={16} style={{ color: '#0176d3' }} />
                      <span>Clone List View</span>
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item
                      onClick={() => {
                        if (buttonLoadingStates.delete || !currentListView || !canDelete) return;
                        handleDeleteListView(currentListView);
                      }}
                      disabled={buttonLoadingStates.delete || !canDelete}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 16px',
                        cursor: (buttonLoadingStates.delete || !canDelete) ? 'not-allowed' : 'pointer',
                        opacity: (buttonLoadingStates.delete || !canDelete) ? 0.6 : 1,
                        color: (buttonLoadingStates.delete || !canDelete) ? '#6c757d' : '#dc3545'
                      }}
                    >
                      {buttonLoadingStates.delete ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '14px', height: '14px' }}></span>
                          <span>Deleting...</span>
                        </>
                      ) : (
                        <>
                          <FaTrash size={16} style={{ color: (buttonLoadingStates.delete || !canDelete) ? '#6c757d' : '#dc3545' }} />
                          <span>Delete List View</span>
                        </>
                      )}
                    </Dropdown.Item>
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              );
            })()}
          </div>
          <div className="sf-actions" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Search Bar */}
            <div className='search-container' style={{ width: '250px', margin: 0, padding: 0 }}>
              <InputGroup style={{ margin: 0 }}>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderRight: 'none',
                    borderTopLeftRadius: '0.375rem',
                    borderBottomLeftRadius: '0.375rem',
                    fontSize: '0.875rem',
                    padding: '0.5rem 0.75rem',
                    height: '38px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <i
                    className='fas fa-search'
                    style={{ color: '#6c757d', fontSize: '0.875rem' }}
                  ></i>
                </InputGroup.Text>
                <Form.Control
                  type='text'
                  placeholder={`Search ${objectLabel.toLowerCase()}...`}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  style={{
                    borderLeft: 'none',
                    borderRight: searchTerm ? 'none' : '1px solid #ced4da',
                    borderTopRightRadius: searchTerm ? '0' : '0.375rem',
                    borderBottomRightRadius: searchTerm ? '0' : '0.375rem',
                    fontSize: '0.875rem',
                    padding: '0.5rem 0.75rem',
                    height: '38px',
                  }}
                />
                {searchTerm && (
                  <InputGroup.Text
                    style={{
                      backgroundColor: '#f8f9fa',
                      borderLeft: 'none',
                      borderTopRightRadius: '0.375rem',
                      borderBottomRightRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem 0.75rem',
                      height: '38px',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() => setSearchTerm('')}
                    title="Clear search"
                  >
                    <FaTimes
                      style={{ color: '#6c757d', fontSize: '0.875rem' }}
                    />
                  </InputGroup.Text>
                )}
              </InputGroup>
            </div>

            {/* View Actions Group - Platform and View Type */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRight: '1px solid #e5e5e5' }}>
              {/* Platform Switcher - Icon Dropdown */}
              <Dropdown onToggle={(isOpen) => { }}>
                <Dropdown.Toggle
                  variant="light"
                  style={{
                    padding: '8px',
                    border: '1px solid #d8dde6',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    color: '#706e6b',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    boxShadow: 'none',
                    outline: 'none'
                  }}
                  title={currentPlatform === 'mobile' ? 'Mobile Platform' : 'Web Platform'}
                >
                  {currentPlatform === 'mobile' ? <FaMobileAlt size={16} /> : <FaDesktop size={16} />}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{
                    minWidth: '180px',
                    border: '1px solid #d8dde6',
                    borderRadius: '4px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    padding: '4px 0',
                    marginTop: '4px'
                  }}
                >
                  <Dropdown.Item
                    onClick={() => {
                      setCurrentPlatform('web');
                      setListViewName("");
                    }}
                    active={currentPlatform === 'web'}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 16px',
                      backgroundColor: currentPlatform === 'web' ? '#e8f4ff' : 'transparent',
                      color: currentPlatform === 'web' ? '#0176d3' : '#032d60',
                      fontWeight: currentPlatform === 'web' ? '600' : '400',
                      cursor: 'pointer'
                    }}
                  >
                    <FaDesktop size={16} style={{ color: currentPlatform === 'web' ? '#0176d3' : '#706e6b' }} />
                    <span>Web Platform</span>
                    {currentPlatform === 'web' && (
                      <span style={{ marginLeft: 'auto', color: '#0176d3' }}>✓</span>
                    )}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setCurrentPlatform('mobile');
                      setListViewName("");
                    }}
                    active={currentPlatform === 'mobile'}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 16px',
                      backgroundColor: currentPlatform === 'mobile' ? '#e8f4ff' : 'transparent',
                      color: currentPlatform === 'mobile' ? '#0176d3' : '#032d60',
                      fontWeight: currentPlatform === 'mobile' ? '600' : '400',
                      cursor: 'pointer'
                    }}
                  >
                    <FaMobileAlt size={16} style={{ color: currentPlatform === 'mobile' ? '#0176d3' : '#706e6b' }} />
                    <span>Mobile Platform</span>
                    {currentPlatform === 'mobile' && (
                      <span style={{ marginLeft: 'auto', color: '#0176d3' }}>✓</span>
                    )}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              {/* View Type Switcher - Icon Dropdown */}
              <Dropdown onToggle={(isOpen) => { }}>
                <Dropdown.Toggle
                  variant="light"
                  disabled={isUpdatingViewType}
                  style={{
                    padding: '8px',
                    border: '1px solid #d8dde6',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    color: '#706e6b',
                    cursor: isUpdatingViewType ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    boxShadow: 'none',
                    outline: 'none'
                  }}
                  title={effectiveViewType === 'kanban' ? 'Kanban View' : 'List View'}
                >
                  {isUpdatingViewType ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '14px', height: '14px' }}></span>
                  ) : (
                    effectiveViewType === 'kanban' ? <FaColumns size={16} /> : <FaList size={16} />
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{
                    minWidth: '180px',
                    border: '1px solid #d8dde6',
                    borderRadius: '4px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    padding: '4px 0',
                    marginTop: '4px',
                    zIndex: 99999
                  }}
                >
                  <Dropdown.Item
                    onClick={() => handleViewTypeChange('list')}
                    active={effectiveViewType === 'list'}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 16px',
                      backgroundColor: effectiveViewType === 'list' ? '#e8f4ff' : 'transparent',
                      color: effectiveViewType === 'list' ? '#0176d3' : '#032d60',
                      fontWeight: effectiveViewType === 'list' ? '600' : '400',
                      cursor: 'pointer'
                    }}
                  >
                    <FaList size={16} style={{ color: effectiveViewType === 'list' ? '#0176d3' : '#706e6b' }} />
                    <span>List</span>
                    {effectiveViewType === 'list' && (
                      <span style={{ marginLeft: 'auto', color: '#0176d3' }}>✓</span>
                    )}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => handleViewTypeChange('kanban')}
                    active={effectiveViewType === 'kanban'}
                    disabled={!canUseKanban && effectiveViewType !== 'kanban'}
                    title={!canUseKanban ? "Configure Kanban view in list view settings" : "Kanban View"}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 16px',
                      backgroundColor: effectiveViewType === 'kanban' ? '#e8f4ff' : 'transparent',
                      color: effectiveViewType === 'kanban' ? '#0176d3' : (!canUseKanban ? '#c9c7c5' : '#032d60'),
                      fontWeight: effectiveViewType === 'kanban' ? '600' : '400',
                      cursor: !canUseKanban && effectiveViewType !== 'kanban' ? 'help' : 'pointer',
                      opacity: !canUseKanban && effectiveViewType !== 'kanban' ? 0.6 : 1
                    }}
                  >
                    <FaColumns size={16} style={{ color: effectiveViewType === 'kanban' ? '#0176d3' : (!canUseKanban ? '#c9c7c5' : '#706e6b') }} />
                    <span>Kanban</span>
                    {effectiveViewType === 'kanban' && (
                      <span style={{ marginLeft: 'auto', color: '#0176d3' }}>✓</span>
                    )}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>

            {/* Data Actions Group */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Filter Button */}
              <button
                onClick={toggleFilterPanel}
                title={hasActiveFilters ? `${activeFiltersCount} active filter${activeFiltersCount !== 1 ? 's' : ''}` : 'Filters'}
                className={`icon-btn ${filterPanelOpen ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
                style={{
                  padding: '8px',
                  border: `1px solid ${hasActiveFilters ? '#0176d3' : '#d8dde6'}`,
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  color: hasActiveFilters ? '#0176d3' : '#706e6b',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '36px',
                  height: '36px',
                  position: 'relative'
                }}
              >
                <FaFilter size={16} />
                {hasActiveFilters && (
                  <span style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    backgroundColor: '#0176d3',
                    color: 'white',
                    borderRadius: '10px',
                    padding: '2px 5px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    minWidth: '16px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              {/* More Actions Dropdown */}
              <Dropdown onToggle={(isOpen) => { }}>
                <Dropdown.Toggle
                  variant="light"
                  className="no-caret"
                  style={{
                    padding: '8px',
                    border: '1px solid #d8dde6',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    color: '#706e6b',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    boxShadow: 'none',
                    outline: 'none'
                  }}
                  title="More Actions"
                >
                  <FaEllipsisV size={16} />
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{
                    minWidth: '200px',
                    border: '1px solid #d8dde6',
                    borderRadius: '4px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    padding: '4px 0',
                    marginTop: '4px'
                  }}
                >
                  {/* Data Actions Section */}
                  <Dropdown.Item
                    onClick={handleImport}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 16px',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-file-import" style={{ color: '#28a745', width: '16px' }}></i>
                    <span>Import</span>
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={handleExport}
                    disabled={selectedRows.length === 0 && records.length === 0}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 16px',
                      cursor: (selectedRows.length === 0 && records.length === 0) ? 'not-allowed' : 'pointer',
                      opacity: (selectedRows.length === 0 && records.length === 0) ? 0.6 : 1
                    }}
                  >
                    <i className="fas fa-download" style={{ color: '#0176d3', width: '16px' }}></i>
                    <span>Export</span>
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={handleBulkCall}
                    disabled={selectedRows.length === 0}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 16px',
                      cursor: selectedRows.length === 0 ? 'not-allowed' : 'pointer',
                      opacity: selectedRows.length === 0 ? 0.6 : 1
                    }}
                  >
                    <i className="fas fa-phone" style={{ color: selectedRows.length === 0 ? '#6c757d' : '#28a745', width: '16px' }}></i>
                    <span>Bulk Call {selectedRows.length > 0 && `(${selectedRows.length})`}</span>
                  </Dropdown.Item>
                  {objectName?.toLowerCase() !== 'user' && (
                    <Dropdown.Item
                      onClick={handleBulkDelete}
                      disabled={selectedRows.length === 0 || isDeleting}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 16px',
                        cursor: (selectedRows.length === 0 || isDeleting) ? 'not-allowed' : 'pointer',
                        opacity: (selectedRows.length === 0 || isDeleting) ? 0.6 : 1,
                        color: (selectedRows.length === 0 || isDeleting) ? '#6c757d' : '#dc3545'
                      }}
                    >
                      {isDeleting ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '14px', height: '14px' }}></span>
                          <span>Deleting...</span>
                        </>
                      ) : (
                        <>
                          <FaTrash size={16} style={{ color: (selectedRows.length === 0 || isDeleting) ? '#6c757d' : '#dc3545' }} />
                          <span>Delete {selectedRows.length > 0 && `(${selectedRows.length})`}</span>
                        </>
                      )}
                    </Dropdown.Item>
                  )}
                  {objectName?.toLowerCase() !== 'email_template' && hasPackage('email_campaign') && (
                    <Dropdown.Item
                      onClick={handleBulkEmail}
                      disabled={false}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 16px',
                        cursor: 'pointer',
                        opacity: 1
                      }}
                    >
                      <i className="fas fa-envelope" style={{ color: selectedRows.length === 0 ? '#6c757d' : '#007bff' }}></i>
                      <span>Email {selectedRows.length > 0 && `(${selectedRows.length})`}</span>
                    </Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown>

              {/* New Record Button - Primary Action with Icon + Label */}
              {objectName?.toLowerCase() !== 'email_log' && (
                <Button
                  variant="primary"
                  className="sf-action-btn"
                  onClick={() => {
                    setEditingRecordId(null);
                    setIsCloneMode(false);
                    setEditModalShow(true);
                    setDropdownOpenIndex(null);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    fontWeight: '500'
                  }}
                >
                  <FaPlus size={14} /> New {objectLabel}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="main-container" style={{ width: '100%', maxWidth: '100%', padding: 0, margin: 0 }}>
          <div style={{ display: 'flex', gap: '0', width: '100%' }}>
            {/* Left Sidebar for Filters */}
            <div className={`filter-sidebar ${filterPanelOpen ? 'open' : ''}`} style={{
              width: filterPanelOpen ? '500px' : '0',
              minWidth: filterPanelOpen ? '500px' : '0',
              maxWidth: filterPanelOpen ? '500px' : '0',
              transition: 'width 0.3s ease, min-width 0.3s ease, max-width 0.3s ease',
              overflow: 'hidden',
              backgroundColor: 'white',
              borderRight: filterPanelOpen ? '1px solid #e5e5e5' : 'none',
              boxShadow: filterPanelOpen ? '2px 0 8px rgba(0, 0, 0, 0.1)' : 'none',
              position: 'relative',
              zIndex: 10,
              height: '100%'
            }}>
              {filterPanelOpen && (
                <ListViewFilterPanel
                  filterPanelOpen={filterPanelOpen}
                  externalFilters={externalFilters}
                  externalFilterLogic={externalFilterLogic}
                  availableFields={availableFields}
                  activeFiltersCount={activeFiltersCount}
                  onClose={() => setFilterPanelOpen(false)}
                  onAddFilter={addExternalFilter}
                  onRemoveFilter={removeExternalFilter}
                  onUpdateFilter={updateExternalFilter}
                  onUpdateLogic={setExternalFilterLogic}
                  getOperatorsForField={getOperatorsForField}
                  isOperatorValueRequired={isOperatorValueRequired}
                  getFieldDataType={getFieldDataType}
                  onApplyFilters={() => applyExternalFilters(setRefreshTrigger)}
                  onClearFilters={() => clearExternalFilters(setRefreshTrigger)}
                  showDateHelpModal={showDateHelpModal}
                  setShowDateHelpModal={setShowDateHelpModal}
                />
              )}
            </div>

            {(permissionChecking || (objectName && records.length === 0 && (loading || !initialLoadComplete))) ? (
              <div style={{ textAlign: 'center', padding: '40px 0', flex: 1 }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading...</p>
              </div>
            ) : effectiveViewType === 'kanban' ? (
              <KanbanView
                records={records}
                kanbanConfig={kanbanConfig}
                objectName={objectName}
                picklistOptions={picklistOptions}
                loading={loading}
                onUpdateRecord={handleKanbanUpdateRecord}
                onEdit={handleKanbanEdit}
                onDelete={handleKanbanDelete}
                onClone={handleKanbanClone}
                onView={(record) => {
                  const recordIdValue = record.id?.value || record.id;
                  window.open(`/view/${objectName}/${recordIdValue}`, '_blank');
                }}
              />
            ) : (
              // Record currency, base equivalent, and current-rate fallback when exchange_rate is missing: ResizableTable
              <div className="resizable-table-wrapper" style={{ flex: 1, width: '100%' }}>
                <ResizableTable
                  data={prepareTableDataLocal()}
                  columns={prepareTableColumns()}
                  loading={loading}
                  error={null}
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  currentPage={1}
                  totalRecords={getFilteredAndSortedRecordsLocal().length}
                  recordsPerPage={getFilteredAndSortedRecordsLocal().length}
                  onPageChange={() => { }}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  showSerialNumber={true}
                  showActions={true}
                  actions={getRowActions()}
                  headerActions={getHeaderActions()}
                  selectedRows={selectedRows}
                  onRowSelectionChange={handleRowSelectionChange}
                  exportFilename={`${objectName}_records`}
                  exportSheetName={objectName}
                  className="listview-resizable-table"
                  tableClassName="table-theme"
                  emptyMessage="No records found"
                  showSearch={false}
                  showPagination={false}
                  searchPlaceholder={`Search ${objectLabel.toLowerCase()}...`}
                  objectName={objectName}
                  sourcePage="listview"
                  onRefresh={() => setRefreshTrigger(prev => prev + 1)}
                  onShowToast={showToastMessage}
                  allObjectFields={allObjectFields}
                  recordLinkField={recordLinkField}
                  triggerImport={triggerImport}
                  onImportTriggered={handleImportTriggered}
                />
              </div>
            )}
          </div>

          {/* Load More Button - Outside flex container, below the table */}
          {hasMore && records.length > 0 && effectiveViewType !== 'kanban' && (
            <div style={{ textAlign: 'center', padding: '20px 0', width: '100%' }}>
              <Button
                variant="outline-primary"
                onClick={loadMore}
                disabled={loading || isLoadingMore}
                style={{ minWidth: '120px' }}
              >
                {isLoadingMore ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading...
                  </>
                ) : (
                  'Load More'
                )}
              </Button>
            </div>
          )}
        </div>



        <EnhancedListViewDialog
          objectName={objectName}
          show={newDialogOpen}
          onClose={() => setNewDialogOpen(false)}
          onSave={handleListViewSave}
          onUpdate={handleListViewUpdate}
          selectedFieldList={fieldOrder}
          editMode={false}
          currentPlatform={currentPlatform}
        />

        <EnhancedListViewDialog
          objectName={objectName}
          show={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingListView(null);
          }}
          onSave={handleListViewSave}
          onUpdate={handleListViewUpdate}
          selectedFieldList={fieldOrder}
          editMode={true}
          listViewData={editingListView}
          currentPlatform={currentPlatform}
        />

        <EnhancedListViewDialog
          objectName={objectName}
          show={cloneDialogOpen}
          onClose={() => {
            setCloneDialogOpen(false);
            setCloningListView(null);
          }}
          onSave={handleListViewSave}
          onUpdate={handleListViewUpdate}
          selectedFieldList={fieldOrder}
          editMode={false}
          listViewData={cloningListView}
          currentPlatform={currentPlatform}
        />

        {/* EditLayout Modal */}
        <EditLayoutModal
          show={editModalShow}
          onHide={() => {
            setEditModalShow(false);
            setIsCloneMode(false);
          }}
          objectName={objectName}
          id={editingRecordId}
          isClone={isCloneMode}
          sourcePage="listview"
          onSave={(savedData) => {
            console.log('Record saved:', savedData);
            const recordId = savedData?.id ?? savedData?.data?.id;
            const isNewRecord = !editingRecordId && !isCloneMode;

            if (isNewRecord && recordId) {
              setEditModalShow(false);
              setIsCloneMode(false);
              navigate(`/view/${objectName}/${recordId}`);
              return;
            }

            // Refresh the list view data after update/clone
            setRefreshTrigger(prev => prev + 1);
            const successMessage = isCloneMode ? "Record cloned successfully!" :
              editingRecordId ? "Record updated successfully!" :
                "Record created successfully!";
            console.log(successMessage);
          }}
          onCancel={() => {
            setEditModalShow(false);
            setIsCloneMode(false);
          }}
        />

        <Modal
          show={showBulkCallModal}
          onHide={closeBulkCallModal}
          size="lg"
          backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fas fa-phone me-2 text-success" />
              Bulk Call
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
              <div>
                <label className="form-label mb-1">Delay between calls (seconds)</label>
                <Form.Control
                  type="number"
                  min={1}
                  max={300}
                  value={bulkCallDelaySeconds}
                  onChange={(e) => setBulkCallDelaySeconds(Number(e.target.value) || 1)}
                  disabled={bulkCallIsRunning && !bulkCallIsPaused}
                  style={{ width: "220px" }}
                />
              </div>
              <div className="text-muted small">
                Next number dials automatically after this delay.
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 mb-3">
              {!bulkCallIsRunning ? (
                <Button variant="success" onClick={startBulkAutoCall} disabled={bulkCallQueue.length === 0}>
                  Start Auto Calling
                </Button>
              ) : bulkCallIsPaused ? (
                <Button variant="primary" onClick={resumeBulkAutoCall}>
                  Resume
                </Button>
              ) : (
                <Button variant="warning" onClick={pauseBulkAutoCall}>
                  Pause
                </Button>
              )}
              <Button
                variant="outline-secondary"
                onClick={() => {
                  if (bulkCallTimerRef.current) {
                    clearTimeout(bulkCallTimerRef.current);
                    bulkCallTimerRef.current = null;
                  }
                  setBulkCallIsRunning(false);
                  setBulkCallIsPaused(false);
                  setBulkCallCurrentIndex(-1);
                }}
              >
                Stop
              </Button>
            </div>

            <div className="small text-muted mb-2">
              {bulkCallCurrentIndex >= 0
                ? `Calling ${bulkCallCurrentIndex + 1} of ${bulkCallQueue.length}`
                : `Queued contacts: ${bulkCallQueue.length}`}
            </div>

            <div style={{ maxHeight: "360px", overflowY: "auto", border: "1px solid #e9ecef", borderRadius: "6px" }}>
              <table className="table table-sm mb-0">
                <thead style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 1 }}>
                  <tr>
                    <th style={{ width: "50px" }}>#</th>
                    <th>Contact</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkCallQueue.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-3">
                        No contacts selected.
                      </td>
                    </tr>
                  ) : (
                    bulkCallQueue.map((entry, index) => (
                      <tr key={entry.id}>
                        <td>{index + 1}</td>
                        <td>{entry.name}</td>
                        <td>{entry.phoneDisplay || "-"}</td>
                        <td>
                          <Badge
                            bg={
                              entry.status === "completed"
                                ? "success"
                                : entry.status === "calling"
                                  ? "primary"
                                  : entry.status === "failed"
                                    ? "danger"
                                    : entry.status === "skipped"
                                      ? "secondary"
                                      : "warning"
                            }
                          >
                            {entry.status}
                          </Badge>
                        </td>
                        <td className="small">{entry.message || "-"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeBulkCallModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Bulk Delete Confirmation Modal: confirm → loader → result (then refresh only on Close) */}
        <Modal show={showDeleteConfirmModal} onHide={cancelBulkDelete} centered backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>
              <FaTrash className="me-2 text-danger" />
              {bulkDeleteResult ? 'Bulk Delete Result' : 'Confirm Bulk Delete'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {bulkDeleteResult ? (
              <div className="bulk-delete-result">
                {bulkDeleteResult.apiError ? (
                  <div className="alert alert-danger mb-0">
                    {bulkDeleteResult.apiError}
                  </div>
                ) : (
                  <>
                    <div className="d-flex flex-wrap gap-3 mb-3">
                      <div className="text-success">
                        <strong>Deleted:</strong> {bulkDeleteResult.deleted} of {bulkDeleteResult.total}
                      </div>
                      {bulkDeleteResult.errors.length > 0 && (
                        <div className="text-danger">
                          <strong>Failed:</strong> {bulkDeleteResult.errors.length}
                        </div>
                      )}
                    </div>
                    {bulkDeleteResult.errors.length > 0 && (
                      <div className="mt-2">
                        <strong className="text-muted">Reasons:</strong>
                        <ul className="list-unstyled mb-0 mt-1 small">
                          {bulkDeleteResult.errors.slice(0, 10).map((e, idx) => (
                            <li key={idx} className="text-danger">
                              Record {e.id}: {e.error}
                            </li>
                          ))}
                          {bulkDeleteResult.errors.length > 10 && (
                            <li className="text-muted">... and {bulkDeleteResult.errors.length - 10} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : isDeleting ? (
              <div className="text-center">
                <div className="spinner-border text-danger mb-3" role="status">
                  <span className="visually-hidden">Deleting...</span>
                </div>
                <p className="mb-2">
                  Deleting {deleteProgress.total} record{deleteProgress.total !== 1 ? 's' : ''}...
                </p>
                <div className="small text-muted">
                  Processed {deleteProgress.processed} / {deleteProgress.total} · Deleted {deleteProgress.deleted} · Failed {deleteProgress.errors}
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="mb-3">
                  <FaTrash size={48} className="text-danger mb-3" />
                </div>
                <h5>Are you sure you want to delete these records?</h5>
                <p className="text-muted">
                  You are about to delete <strong>{selectedRows.length}</strong> record{selectedRows.length !== 1 ? 's' : ''} from <strong>{objectLabel}</strong>.
                </p>
                <div className="alert alert-warning">
                  <strong>Warning:</strong> This action cannot be undone. The selected records will be permanently deleted.
                </div>
                <div className="form-check mt-3 text-start">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="listview-bulk-delete-children-checkbox"
                    checked={bulkDeleteChildrenChecked}
                    onChange={e => setBulkDeleteChildrenChecked(e.target.checked)}
                    disabled={isDeleting}
                  />
                  <label className="form-check-label" htmlFor="listview-bulk-delete-children-checkbox">
                    Also delete related (child) records
                  </label>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            {bulkDeleteResult ? (
              <Button variant="primary" onClick={cancelBulkDelete}>
                OK
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={cancelBulkDelete} disabled={isDeleting}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={confirmBulkDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FaTrash className="me-2" />
                      Delete {selectedRows.length} Record{selectedRows.length !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </>
            )}
          </Modal.Footer>
        </Modal>

        <Modal
          show={showEmailCampaignModal}
          onHide={() => setShowEmailCampaignModal(false)}
          size="xl"
          backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title>Email Campaign</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <EmailCampaignAudienceContentTab
              recordData={campaignRecordData}
              objectName="email_campaign"
              recordId={campaignRecordId}
              defaultTargetObject={objectName}
              defaultListViewName={listViewName}
              selectedRecordIds={selectedRows}
              showSaveDraft={false}
            />
          </Modal.Body>
        </Modal>

        {/* Bulk Delete Progress Modal (only when confirm modal is not open, e.g. other entry points) */}
        <Modal
          show={isDeleting && !showDeleteConfirmModal}
          centered
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header>
            <Modal.Title>Deleting Records</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <div className="spinner-border text-danger mb-3" role="status">
                <span className="visually-hidden">Deleting...</span>
              </div>
              <p className="mb-2">
                Deleting {deleteProgress.total} record{deleteProgress.total !== 1 ? 's' : ''}...
              </p>
              <div className="small text-muted">
                Processed {deleteProgress.processed} / {deleteProgress.total} ·
                Deleted {deleteProgress.deleted} ·
                Failed {deleteProgress.errors}
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* Kanban Configuration Help Modal */}
        <Modal
          show={showKanbanHelpModal}
          onHide={() => setShowKanbanHelpModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <FaColumns className="me-2 text-primary" />
              Enable Kanban View
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              This list view doesn’t have a Kanban configuration yet. To enable Kanban:
            </p>
            <ol style={{ paddingLeft: '1.2rem', marginTop: '1rem' }}>
              <li>Open the list view editor (Edit List View).</li>
              <li>Set a <strong>Group By</strong> field under the Kanban settings.</li>
              <li>(Optional) Configure card fields, colors, and sorting.</li>
              <li>Save the list view, then switch back to Kanban.</li>
            </ol>
            <p className="mt-3 text-muted" style={{ fontSize: '13px' }}>
              The Group By field must be a picklist-type field so records can be bucketed into columns.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowKanbanHelpModal(false)}>
              Got it
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setShowKanbanHelpModal(false);
                const currentListView = listViews.find(view => view.name === listViewName);
                if (currentListView) {
                  handleEditListView(currentListView);
                } else {
                  setEditDialogOpen(true);
                }
              }}
            >
              Configure Now
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Date Filter Help Modal */}
        <Modal show={showDateHelpModal} onHide={() => setShowDateHelpModal(false)} size="lg" centered className="date-help-modal">
          <Modal.Header closeButton>
            <Modal.Title>
              <FaQuestionCircle className="me-2 text-primary" />
              Date Filter Help
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6">
                <h6 className="text-primary mb-3">📅 Date Formats</h6>
                <div className="mb-3">
                  <strong>Specific Dates:</strong>
                  <ul className="mt-2">
                    <li><code>2024-01-15</code> - January 15, 2024</li>
                    <li><code>2024-12-31</code> - December 31, 2024</li>
                  </ul>
                </div>

                <div className="mb-3">
                  <strong>Between Operator:</strong>
                  <ul className="mt-2">
                    <li><code>2024-01-01|2024-01-31</code> - January 2024</li>
                    <li><code>2024-06-01|2024-06-30</code> - June 2024</li>
                  </ul>
                </div>
              </div>

              <div className="col-md-6">
                <h6 className="text-primary mb-3">🎯 Date Literals</h6>
                <div className="mb-3">
                  <strong>Basic Literals:</strong>
                  <ul className="mt-2">
                    <li><code>TODAY</code> - Current date</li>
                    <li><code>YESTERDAY</code> - Previous day</li>
                    <li><code>TOMORROW</code> - Next day</li>
                  </ul>
                </div>

                <div className="mb-3">
                  <strong>Week Literals:</strong>
                  <ul className="mt-2">
                    <li><code>THIS_WEEK</code> - Current week</li>
                    <li><code>LAST_WEEK</code> - Previous week</li>
                    <li><code>NEXT_WEEK</code> - Next week</li>
                  </ul>
                </div>

                <div className="mb-3">
                  <strong>Month Literals:</strong>
                  <ul className="mt-2">
                    <li><code>THIS_MONTH</code> - Current month</li>
                    <li><code>LAST_MONTH</code> - Previous month</li>
                    <li><code>NEXT_MONTH</code> - Next month</li>
                  </ul>
                </div>

                <div className="mb-3">
                  <strong>Quarter Literals:</strong>
                  <ul className="mt-2">
                    <li><code>THIS_QUARTER</code> - Current quarter</li>
                    <li><code>LAST_QUARTER</code> - Previous quarter</li>
                    <li><code>NEXT_QUARTER</code> - Next quarter</li>
                  </ul>
                </div>

                <div className="mb-3">
                  <strong>Year Literals:</strong>
                  <ul className="mt-2">
                    <li><code>THIS_YEAR</code> - Current year</li>
                    <li><code>LAST_YEAR</code> - Previous year</li>
                    <li><code>NEXT_YEAR</code> - Next year</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-12">
                <h6 className="text-primary mb-3">🔢 N-Period Literals</h6>
                <div className="row">
                  <div className="col-md-4">
                    <strong>Days:</strong>
                    <ul className="mt-2">
                      <li><code>LAST_N_DAYS:7</code> - Last 7 days</li>
                      <li><code>LAST_N_DAYS:30</code> - Last 30 days</li>
                      <li><code>NEXT_N_DAYS:7</code> - Next 7 days</li>
                      <li><code>NEXT_N_DAYS:30</code> - Next 30 days</li>
                    </ul>
                  </div>
                  <div className="col-md-4">
                    <strong>Weeks:</strong>
                    <ul className="mt-2">
                      <li><code>LAST_N_WEEKS:2</code> - Last 2 weeks</li>
                      <li><code>LAST_N_WEEKS:4</code> - Last 4 weeks</li>
                      <li><code>NEXT_N_WEEKS:2</code> - Next 2 weeks</li>
                      <li><code>NEXT_N_WEEKS:4</code> - Next 4 weeks</li>
                    </ul>
                  </div>
                  <div className="col-md-4">
                    <strong>Months/Years:</strong>
                    <ul className="mt-2">
                      <li><code>LAST_N_MONTHS:3</code> - Last 3 months</li>
                      <li><code>LAST_N_MONTHS:6</code> - Last 6 months</li>
                      <li><code>LAST_N_YEARS:1</code> - Last 1 year</li>
                      <li><code>LAST_N_YEARS:2</code> - Last 2 years</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="alert alert-info mt-3">
              <strong>💡 Tips:</strong>
              <ul className="mb-0 mt-2">
                <li>Use specific dates (YYYY-MM-DD) for exact date matching</li>
                <li>Use date literals for relative date filtering</li>
                <li>For "Between" operator, use format: <code>start_date|end_date</code></li>
                <li>All date literals are case-insensitive</li>
              </ul>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDateHelpModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Toast Notification */}
        <AppToast
          key={toastId}
          show={toast.show}
          message={toast.message}
          variant={toast.variant}
          sticky={toast.variant === "danger"}
          onClose={() => setToast((prev) => ({ ...prev, show: false }))}
        />
      </div >
    </>
  );
};

export default ListViewLayout;
